import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'wouter'
import { BellIcon } from '@heroicons/react/24/outline'
import { notificationService, type AppNotification } from '@/services/notificationService'
import { useUser } from '@/store/authStore'

const POLL_MS = 20000

function describe(n: AppNotification): string {
  const name = typeof n.payload.name === 'string' ? n.payload.name : 'Birisi'
  switch (n.type) {
    case 'friend_request':
      return `${name} sana arkadaşlık isteği gönderdi`
    case 'friend_accepted':
      return `${name} arkadaşlık isteğini kabul etti`
    case 'wall_comment':
      return `${name} duvar gönderine yorum yaptı`
    case 'dm':
      return `${name} sana mesaj gönderdi`
    case 'report_resolved':
      return 'Şikayetin incelendi'
    case 'ritual_mission':
      return typeof n.payload.text === 'string' ? n.payload.text : 'Görevin hazır 🎯'
    case 'ritual_checkin':
      return typeof n.payload.text === 'string' ? n.payload.text : 'Akşam yoklaması seni bekliyor'
    default:
      return 'Yeni bir bildirimin var'
  }
}

function iconFor(n: AppNotification): string {
  switch (n.type) {
    case 'friend_request':
    case 'friend_accepted':
      return '👥'
    case 'wall_comment':
      return '💬'
    case 'dm':
      return '✉️'
    case 'report_resolved':
      return '🛡️'
    case 'ritual_mission':
      return '🎯'
    case 'ritual_checkin':
      return '🌙'
    default:
      return '🔔'
  }
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso.replace(' ', 'T') + 'Z').getTime()
  const minutes = Math.floor(diff / 60000)
  if (minutes < 1) return 'az önce'
  if (minutes < 60) return `${minutes} dk önce`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} sa önce`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days} gün önce`
  return new Date(iso.replace(' ', 'T') + 'Z').toLocaleDateString('tr-TR')
}

export const NotificationBell = () => {
  const user = useUser()
  const [, navigate] = useLocation()
  const [open, setOpen] = useState(false)
  const [items, setItems] = useState<AppNotification[]>([])
  const [unread, setUnread] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const refresh = useCallback(async () => {
    try {
      const result = await notificationService.fetch(20)
      setItems(result.notifications)
      setUnread(result.unread)
      setError(null)
    } catch {
      setError('Bildirimler yüklenemedi.')
    }
  }, [])

  useEffect(() => {
    if (!user) return
    void refresh()
    const timer = setInterval(() => void refresh(), POLL_MS)
    return () => clearInterval(timer)
  }, [user, refresh])

  useEffect(() => {
    if (!open) return
    const onClick = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  if (!user) return null

  const toggle = async () => {
    const next = !open
    setOpen(next)
    if (next) {
      setLoading(true)
      await refresh()
      setLoading(false)
    }
  }

  const markAllRead = async () => {
    try {
      await notificationService.markAllRead()
      setItems((prev) => prev.map((item) => ({ ...item, read_at: item.read_at ?? new Date().toISOString() })))
      setUnread(0)
    } catch {
      setError('Bildirimler işaretlenemedi.')
    }
  }

  const openItem = async (item: AppNotification) => {
    if (item.type === 'ritual_mission') {
      setOpen(false)
      navigate('/student')
      return
    }
    if (item.type === 'ritual_checkin') {
      setOpen(false)
      try {
        await notificationService.markRead([item.id])
      } catch {
        void 0
      }
      void refresh()
      navigate('/student?ritual=checkin')
      return
    }
    if (item.type !== 'dm' || typeof item.payload.thread_id !== 'number') return
    setOpen(false)
    try {
      await notificationService.markRead([item.id])
    } catch {
      void 0
    }
    void refresh()
    navigate(`/student/mesajlar/${item.payload.thread_id}`)
  }

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => void toggle()}
        aria-label="Bildirimler"
        className="relative p-2 rounded-full hover:bg-gray-100 transition-colors"
      >
        <BellIcon className="w-6 h-6 text-gray-600" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <p className="font-semibold text-sm text-gray-900">Bildirimler</p>
            {unread > 0 && (
              <button
                type="button"
                onClick={() => void markAllRead()}
                className="text-xs text-blue-600 hover:underline"
              >
                Tümünü okundu işaretle
              </button>
            )}
          </div>

          {loading && <p className="px-4 py-6 text-sm text-gray-500 text-center">Yükleniyor…</p>}

          {!loading && error && (
            <p className="px-4 py-6 text-sm text-red-600 text-center">{error}</p>
          )}

          {!loading && !error && items.length === 0 && (
            <p className="px-4 py-6 text-sm text-gray-500 text-center">Henüz bildirimin yok.</p>
          )}

          {!loading && !error && items.map((item) => {
            const clickable =
              item.type === 'ritual_mission' ||
              item.type === 'ritual_checkin' ||
              (item.type === 'dm' && typeof item.payload.thread_id === 'number')
            return (
              <div
                key={item.id}
                onClick={() => void openItem(item)}
                className={`flex items-start gap-3 px-4 py-3 border-b border-gray-50 last:border-b-0 ${item.read_at ? '' : 'bg-blue-50/60'} ${clickable ? 'cursor-pointer hover:bg-gray-50' : ''}`}
              >
                <span className="text-lg leading-none mt-0.5">{iconFor(item)}</span>
                <div className="min-w-0">
                  <p className="text-sm text-gray-800 break-words">{describe(item)}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{relativeTime(item.created_at)}</p>
                </div>
                {!item.read_at && <span className="ml-auto mt-1.5 w-2 h-2 rounded-full bg-blue-500 shrink-0" />}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
